/**
 * ODDFISH — Contact forms v20260706.01 — AU phone: E.164 into +61 embed widget
 */
(function () {
  "use strict";

  var FORMS_API = "https://forms.shopifyapps.com/api/v2";
  var DEFAULT_FORM_ID = "1041826";
  var instancesCache = null;
  var formsRestrictedCache = null;
  var lastFormsApiError = "";

  function getFieldValue(form, selector) {
    var field = form.querySelector(selector);
    return field ? field.value.trim() : "";
  }

  function getInterests(form) {
    var checked = form.querySelectorAll('input[type="checkbox"][name^="contact["]:checked');
    if (!checked.length) return "";
    return Array.prototype.map
      .call(checked, function (el) {
        return el.value.trim();
      })
      .filter(Boolean)
      .join(", ");
  }

  function getContactFormData(form) {
    var data = {
      name: getFieldValue(form, '[name="contact[name]"]'),
      email: getFieldValue(form, '[name="contact[email]"]'),
      phone: getFieldValue(form, '[name="contact[phone]"]'),
      company: getFieldValue(form, '[name="contact[company]"]'),
      message: getFieldValue(form, '[name="contact[body]"]'),
      interests: getInterests(form),
      hp: getFieldValue(form, '[name="contact[oddfish_hp]"]')
    };

    if (data.interests) {
      data.messageWithInterests = data.message
        ? data.message + "\n\nInterested in: " + data.interests
        : "Interested in: " + data.interests;
    } else {
      data.messageWithInterests = data.message;
    }

    return data;
  }

  function getFormId(root) {
    var id = (root.getAttribute("data-shopify-form-id") || "").trim();
    if (id) return id;
    if (root.getAttribute("data-contact-source") === "home_contact") return "";
    return DEFAULT_FORM_ID;
  }

  function isThemeEditor() {
    return !!(window.Shopify && window.Shopify.designMode);
  }

  /** Shopify native contact redirect adds ?contact_posted=true — strip it so refresh hides the message. */
  function clearShopifyContactPostedParam() {
    if (!window.history || !window.history.replaceState) return;

    try {
      var url = new URL(window.location.href);
      if (!url.searchParams.has("contact_posted")) return;
      url.searchParams.delete("contact_posted");
      var clean = url.pathname + url.search + url.hash;
      history.replaceState(null, "", clean);
    } catch (error) {
      /* ignore */
    }
  }

  clearShopifyContactPostedParam();

  function escapeHtml(value) {
    return String(value)
      .replace(/&/g, "&amp;")
      .replace(/\u003c/g, "&lt;")
      .replace(/\u003e/g, "&gt;")
      .replace(/"/g, "&quot;");
  }

  function normalizeLabel(value) {
    return String(value || "")
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, " ")
      .trim();
  }

  function getShopDomain() {
    return (window.Shopify && window.Shopify.shop) || "";
  }

  function digitsOnly(value) {
    return String(value || "").replace(/\D/g, "");
  }

  /** E.164 for Shopify Forms — AU local numbers (04xx, 02xx, 1300…) → +61… for the embed phone widget. */
  function formatPhoneForForms(phone) {
    var value = String(phone || "").trim();
    if (!value) return "";

    if (value.charAt(0) === "+") {
      var intlDigits = digitsOnly(value.slice(1));
      return intlDigits ? "+" + intlDigits : "";
    }

    if (value.indexOf("00") === 0) {
      var fromDoubleZero = digitsOnly(value.slice(2));
      return fromDoubleZero ? "+" + fromDoubleZero : "";
    }

    var digits = digitsOnly(value);
    if (!digits) return "";

    // Country code already present without +: 61412345678
    if (digits.indexOf("61") === 0 && digits.length >= 11) return "+" + digits;

    // National format with leading 0: 04xx, 02xx, 1300…
    if (digits.charAt(0) === "0") return "+61" + digits.slice(1);

    // 1300 / 1800 without leading 0
    if (digits.indexOf("1300") === 0 && digits.length === 10) return "+61" + digits;
    if (digits.indexOf("1800") === 0 && digits.length === 10) return "+61" + digits;

    // 9-digit AU NSN without leading 0: mobile 4xx… or landline 2/3/7/8 xx…
    if (digits.length === 9 && "23478".indexOf(digits.charAt(0)) > -1) return "+61" + digits;

    return "+" + digits;
  }

  /** National display format (0412… / 0243…) — some Forms embed widgets accept this better than E.164. */
  function formatPhoneNationalForDisplay(phone) {
    var formatted = formatPhoneForForms(phone);
    var digits = digitsOnly(formatted);
    if (digits.indexOf("61") === 0 && digits.length >= 11) {
      return "0" + digits.slice(2);
    }
    return String(phone || "").trim();
  }

  /** Valid Australian number after normalisation (mobile, landline, 1300/1800). */
  function isPhoneValidForForms(phone) {
    var formatted = formatPhoneForForms(phone);
    if (!formatted || formatted === "+61") return false;

    var digits = digitsOnly(formatted);
    if (digits.indexOf("61") !== 0) return false;

    var nsn = digits.slice(2);
    if (nsn.length === 9 && "23478".indexOf(nsn.charAt(0)) > -1) return true;
    if (nsn.length === 10 && (nsn.indexOf("1300") === 0 || nsn.indexOf("1800") === 0)) return true;

    return false;
  }

  function getPhoneValidationMessage(phone) {
    if (!String(phone || "").trim()) return "Please enter your mobile number.";
    if (!isPhoneValidForForms(phone)) {
      return "Please enter a valid Australian phone number (e.g. 0412 345 678 or 02 4365 6782).";
    }
    return "";
  }

  function fetchFormInstances() {
    if (instancesCache) return Promise.resolve(instancesCache);

    var shop = getShopDomain();
    if (!shop) return Promise.resolve([]);

    return fetch(FORMS_API + "/form_instances?shopify_domain=" + encodeURIComponent(shop), {
      headers: { Accept: "application/json" },
      credentials: "omit"
    })
      .then(function (res) {
        if (!res.ok) return [];
        return res.json();
      })
      .then(function (data) {
        instancesCache = Array.isArray(data) ? data : [];
        return instancesCache;
      })
      .catch(function () {
        instancesCache = [];
        return [];
      });
  }

  function getFormInstance(formId) {
    return fetchFormInstances().then(function (instances) {
      for (var i = 0; i < instances.length; i++) {
        if (String(instances[i].id) === String(formId)) return instances[i];
      }
      return null;
    });
  }

  function valueForFieldDef(field, data) {
    var type = String(field.type || "").toLowerCase();
    var label = normalizeLabel(field.label || field.title || field.name || "");
    var key = String(field.key || field.id || field.name || "").toLowerCase();
    var message = data.messageWithInterests || data.message;

    if (type === "email" || key.indexOf("email") > -1) return data.email;
    if (
      key.indexOf("intrest") > -1 ||
      key.indexOf("interest") > -1 ||
      label.indexOf("intrest") > -1 ||
      label.indexOf("interest") > -1
    ) {
      return data.interests;
    }
    if (
      type === "tel" ||
      key.indexOf("phone") > -1 ||
      label.indexOf("phone") > -1 ||
      label.indexOf("mobile") > -1
    ) {
      return formatPhoneForForms(data.phone);
    }
    if (type === "textarea" || key.indexOf("message") > -1 || label.indexOf("message") > -1) {
      return message;
    }
    if (type === "text") {
      if (key.indexOf("company") > -1 || label.indexOf("company") > -1) return data.company;
      if (key.indexOf("first") > -1 || key.indexOf("name") > -1 || label.indexOf("name") > -1) {
        return data.name;
      }
    }
    return "";
  }

  function buildPayloadFromInstance(formId, data, instance) {
    var shop = getShopDomain();
    var payload = {
      shopify_domain: shop,
      form_instance_id: Number(formId),
      customer_consented_to_email_marketing: true,
      customer_consented_to_sms_marketing: false
    };

    var fields =
      (instance &&
        instance.formDefinition &&
        instance.formDefinition.form &&
        instance.formDefinition.form.fields) ||
      [];

    fields.forEach(function (field) {
      var fieldKey = field.key || field.id;
      var value = valueForFieldDef(field, data);
      if (fieldKey && value) payload[fieldKey] = value;
    });

    if (data.email && !payload.email) payload.email = data.email;

    return payload;
  }

  function buildFallbackPayloads(formId, data) {
    var shop = getShopDomain();
    var message = data.messageWithInterests || data.message;
    var phone = formatPhoneForForms(data.phone);
    var base = {
      shopify_domain: shop,
      form_instance_id: Number(formId),
      customer_consented_to_email_marketing: true,
      customer_consented_to_sms_marketing: false
    };

    return [
      Object.assign({}, base, {
        email: data.email,
        first_name: data.name,
        phone_number: phone,
        "custom#message": message,
        "custom#intrests": data.interests
      }),
      Object.assign({}, base, {
        email: data.email,
        first_name: data.name,
        phone: formatPhoneForForms(data.phone) || data.phone,
        phone_number: phone,
        company: data.company,
        message: message
      }),
      Object.assign({}, base, {
        email: data.email,
        firstName: data.name,
        phone: phone,
        message: message
      })
    ];
  }

  function markFormsApiFailure(body, status) {
    var message = body && body.message ? String(body.message) : "";
    lastFormsApiError = message || (status ? "HTTP " + status : "Forms request failed");

    var lower = message.toLowerCase();
    if (
      status === 403 ||
      lower.indexOf("permission") > -1 ||
      lower.indexOf("access restricted") > -1
    ) {
      formsRestrictedCache = true;
    }
  }

  function getEmbedCaptchaToken(root) {
    var host = getFormsCaptureHost(root);
    if (!host) return "";

    var embed = host.querySelector("shopify-forms-embed, form-embed");
    if (!embed || !embed.shadowRoot) return "";

    var field = embed.shadowRoot.querySelector('[name="h-captcha-response"]');
    return field && field.value ? String(field.value).trim() : "";
  }

  function appendCaptchaToken(payload, root) {
    var token = getEmbedCaptchaToken(root);
    if (token) payload["h-captcha-response"] = token;
    return payload;
  }

  function postFormSubmission(payload, root) {
    var requestPayload = appendCaptchaToken(Object.assign({}, payload), root);

    return fetch(FORMS_API + "/form_submission", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      credentials: "include",
      body: JSON.stringify(requestPayload)
    })
      .then(function (res) {
        return res
          .json()
          .catch(function () {
            return {};
          })
          .then(function (body) {
            if (!res.ok) markFormsApiFailure(body, res.status);
            return { ok: res.ok, status: res.status, body: body };
          });
      })
      .catch(function () {
        lastFormsApiError = "Network error contacting Apps → Forms";
        return { ok: false, status: 0, body: {} };
      });
  }

  function submitViaFormsApi(root, formId, data, instance) {
    return submitViaHiddenFormsEmbed(root, data);
  }

  function triggerEmbedSubmit(shadowRoot, submitBtn, shadowForm) {
    if (shadowForm && typeof shadowForm.requestSubmit === "function") {
      shadowForm.requestSubmit(submitBtn || undefined);
    } else if (submitBtn) {
      submitBtn.click();
    }
  }

  /** Run invisible hCaptcha on the hidden embed; return token for server-side Forms POST. */
  function acquireEmbedCaptchaToken(root, timeoutMs) {
    timeoutMs = timeoutMs || 20000;
    var host = getFormsCaptureHost(root);
    if (!host) return Promise.resolve("");

    return waitForFormsEmbed(host, 10000).then(function (embed) {
      if (!embed || !embed.shadowRoot) return "";

      var shadowRoot = embed.shadowRoot;
      var existing = shadowRoot.querySelector('textarea[name="h-captcha-response"]');
      if (existing && existing.value && existing.value.length > 20) {
        return existing.value;
      }

      var submitBtn = shadowRoot.querySelector(
        '[data-testid="btn-form-submit"], button[type="submit"], input[type="submit"], [data-testid="submit-button"]'
      );
      var shadowForm = shadowRoot.querySelector("form");
      if (submitBtn || shadowForm) {
        triggerEmbedSubmit(shadowRoot, submitBtn, shadowForm);
      }

      return new Promise(function (resolve) {
        var start = Date.now();

        function poll() {
          var cap = shadowRoot.querySelector('textarea[name="h-captcha-response"]');
          if (cap && cap.value && cap.value.length > 20) return resolve(cap.value);
          if (Date.now() - start > timeoutMs) return resolve("");
          window.setTimeout(poll, 200);
        }

        poll();
      });
    });
  }

  function saveViaBackendApi(root, data, formId, hcaptchaToken) {
    var apiUrl = (root.getAttribute("data-contact-api-url") || "").trim();
    if (!apiUrl) return Promise.resolve({ metaOk: false, formsOk: false });

    var secret = (root.getAttribute("data-contact-api-secret") || "").trim();
    var token = hcaptchaToken || "";
    var submitForms = token.length > 20;

    return fetch(apiUrl, {
      method: "POST",
      headers: Object.assign(
        { "Content-Type": "application/json" },
        secret ? { "X-Contact-Secret": secret } : {}
      ),
      body: JSON.stringify({
        name: data.name,
        email: data.email,
        phone: formatPhoneForForms(data.phone) || data.phone,
        company: data.company,
        message: data.message,
        interests: data.interests,
        source: root.getAttribute("data-contact-source") || "website",
        shop: getShopDomain(),
        form_instance_id: formId,
        submit_to_shopify_forms: submitForms,
        hcaptcha_token: token
      })
    })
      .then(function (res) {
        return res.json().catch(function () {
          return null;
        });
      })
      .then(function (payload) {
        var metaOk = !!(payload && payload.ok && payload.id);
        var formsOk = !!(payload && payload.formsOk);

        if (payload && payload.formsError) {
          lastFormsApiError = String(payload.formsError);
        }

        if (metaOk) {
          try {
            sessionStorage.setItem("oddfish_contact_api_ok", "true");
          } catch (error) {
            /* ignore */
          }
        }

        return { metaOk: metaOk, formsOk: formsOk };
      })
      .catch(function () {
        return { metaOk: false, formsOk: false };
      });
  }

  function watchEmbedSubmission(embed, timeoutMs) {
    return Promise.race([
      monitorEmbedSubmission(embed, timeoutMs),
      monitorFormsApiSubmission(timeoutMs)
    ]);
  }

  function resetEmbedIfStaleSuccess(embed) {
    if (!embed || !embed.shadowRoot) return;
    if (!shadowShowsSubmissionSuccess(embed.shadowRoot)) return;
    clearShadowFormFields(embed.shadowRoot);
    var cap = embed.shadowRoot.querySelector('textarea[name="h-captcha-response"]');
    if (cap) cap.value = "";
  }

  /** Hidden embed submit — no captcha UI; invisible hCaptcha runs automatically. */
  function submitViaHiddenFormsEmbed(root, data) {
    var host = getFormsCaptureHost(root);
    if (!host) return Promise.resolve(false);

    var EMBED_WAIT_MS = 10000;
    var SUBMIT_WATCH_MS = 12000;
    var MAX_ATTEMPTS = 3;

    return waitForFormsEmbed(host, EMBED_WAIT_MS).then(function (embed) {
      if (!embed || !embed.shadowRoot) return false;

      resetEmbedIfStaleSuccess(embed);

      var shadowRoot = embed.shadowRoot;
      var submitBtn = shadowRoot.querySelector(
        '[data-testid="btn-form-submit"], button[type="submit"], input[type="submit"], [data-testid="submit-button"]'
      );
      var shadowForm = shadowRoot.querySelector("form");
      if (!submitBtn && !shadowForm) return false;

      function attemptSubmit() {
        return fillShadowForm(shadowRoot, data).then(function (filled) {
          if (!filled) {
            return fillShadowForm(shadowRoot, data);
          }
          return filled;
        }).then(function (filled) {
          if (!filled) return false;

          var verifyPromise = watchEmbedSubmission(embed, SUBMIT_WATCH_MS);
          triggerEmbedSubmit(shadowRoot, submitBtn, shadowForm);
          return verifyPromise;
        });
      }

      function runAttempts(remaining) {
        return attemptSubmit().then(function (ok) {
          if (ok || remaining <= 1) return ok;
          return runAttempts(remaining - 1);
        });
      }

      return runAttempts(MAX_ATTEMPTS);
    });
  }

  var FILL_SETTLE_MS = 400;

  function delay(ms) {
    return new Promise(function (resolve) {
      window.setTimeout(resolve, ms);
    });
  }

  function setNativeFieldValue(field, value) {
    if (!field) return;

    var proto =
      field.tagName === "TEXTAREA"
        ? window.HTMLTextAreaElement.prototype
        : window.HTMLInputElement.prototype;
    var descriptor = Object.getOwnPropertyDescriptor(proto, "value");

    field.focus();
    if (descriptor && descriptor.set) {
      descriptor.set.call(field, value);
    } else {
      field.value = value;
    }

    field.dispatchEvent(
      new InputEvent("input", {
        bubbles: true,
        cancelable: true,
        inputType: "insertText",
        data: String(value)
      })
    );
    field.dispatchEvent(new Event("change", { bubbles: true }));
    field.dispatchEvent(new Event("blur", { bubbles: true }));
  }

  function setShadowFieldValue(root, selectors, value) {
    if (!root || !value) return false;
    for (var i = 0; i < selectors.length; i++) {
      var field = root.querySelector(selectors[i]);
      if (!field) continue;
      setNativeFieldValue(field, value);
      return true;
    }
    return false;
  }

  function waitForFormsEmbed(host, maxMs) {
    maxMs = maxMs || 20000;
    var start = Date.now();

    return new Promise(function (resolve) {
      function findEmbed() {
        if (host) {
          var scoped = host.querySelector("shopify-forms-embed, form-embed");
          if (scoped) return scoped;
        }
        return document.querySelector(
          ".oddfish-shopify-forms-admin-capture shopify-forms-embed, .oddfish-shopify-forms-admin-capture form-embed"
        );
      }

      function check() {
        var embed = findEmbed();
        if (embed && embed.shadowRoot) return resolve(embed);

        if (Date.now() - start > maxMs) return resolve(null);
        window.setTimeout(check, 120);
      }

      check();
    });
  }

  function getFormsCaptureHost(root) {
    if (root) {
      var scoped = root.querySelector(".oddfish-shopify-forms-admin-capture");
      if (scoped) return scoped;
    }
    return document.querySelector(".oddfish-shopify-forms-admin-capture");
  }

  function shadowShowsSubmissionSuccess(shadowRoot) {
    if (!shadowRoot) return false;
    var text = (shadowRoot.textContent || "").toLowerCase();
    if (
      text.indexOf("thank you") > -1 ||
      text.indexOf("thanks for") > -1 ||
      text.indexOf("submitted") > -1 ||
      text.indexOf("subscribed") > -1 ||
      text.indexOf("we'll be in touch") > -1 ||
      text.indexOf("we will be in touch") > -1
    ) {
      return true;
    }
    return !!(
      shadowRoot.querySelector("[data-testid='success-message']") ||
      shadowRoot.querySelector(".form-success") ||
      shadowRoot.querySelector("[role='status']")
    );
  }

  function monitorEmbedSubmission(embed, timeoutMs) {
    timeoutMs = timeoutMs || 15000;

    return new Promise(function (resolve) {
      var done = false;
      var observer = null;

      function finish(ok) {
        if (done) return;
        done = true;
        if (observer) observer.disconnect();
        resolve(!!ok);
      }

      function check() {
        if (embed && shadowShowsSubmissionSuccess(embed.shadowRoot)) {
          finish(true);
        }
      }

      check();

      if (embed && embed.shadowRoot) {
        observer = new MutationObserver(check);
        observer.observe(embed.shadowRoot, {
          childList: true,
          subtree: true,
          characterData: true,
          attributes: true
        });
      }

      window.setTimeout(function () {
        finish(false);
      }, timeoutMs);
    });
  }

  function clearShadowFormFields(shadowRoot) {
    if (!shadowRoot) return;
    shadowRoot.querySelectorAll("input:not([type='hidden']), textarea").forEach(function (field) {
      if (field.name === "h-captcha-response") return;
      setNativeFieldValue(field, "");
    });
  }

  function fillShadowPhoneField(shadowRoot, data) {
    var selectors = [
      '[data-testid="field-phone_number"]',
      'input[type="tel"]',
      'input[name*="phone" i]'
    ];
    var attempts = [];
    var e164 = formatPhoneForForms(data.phone);
    var national = formatPhoneNationalForDisplay(data.phone);
    var nationalDigits = digitsOnly(national);

    [e164, national, nationalDigits].forEach(function (candidate) {
      if (candidate && attempts.indexOf(candidate) === -1) attempts.push(candidate);
    });

    for (var i = 0; i < attempts.length; i++) {
      setShadowFieldValue(shadowRoot, selectors, attempts[i]);
      var current = readShadowFieldValue(shadowRoot, selectors);
      if (current && isPhoneValidForForms(current)) return true;
      if (current && isPhoneValidForForms(data.phone)) return true;
    }

    return false;
  }

  function fillShadowForm(shadowRoot, data) {
    var message = data.messageWithInterests || data.message;

    clearShadowFormFields(shadowRoot);

    setShadowFieldValue(shadowRoot, ['[data-testid="field-first_name"]'], data.name);
    setShadowFieldValue(
      shadowRoot,
      ['[data-testid="field-email"]', 'input[type="email"]', 'input[name*="email" i]'],
      data.email
    );
    fillShadowPhoneField(shadowRoot, data);
    setShadowFieldValue(
      shadowRoot,
      ['[data-testid="field-custom#company"]', 'input[name*="company" i]'],
      data.company
    );
    setShadowFieldValue(
      shadowRoot,
      ['textarea[name="custom#message"]', 'textarea[name*="message" i]', "textarea"],
      message
    );
    setShadowFieldValue(
      shadowRoot,
      ['[data-testid="field-custom#intrests"]', 'input[name*="intrest" i]', 'input[name*="interest" i]'],
      data.interests
    );

    if (data.interests) {
      shadowRoot.querySelectorAll('input[type="checkbox"]').forEach(function (checkbox) {
        var label = normalizeLabel(
          checkbox.getAttribute("aria-label") ||
            checkbox.value ||
            (checkbox.id && shadowRoot.querySelector('label[for="' + checkbox.id + '"]')
              ? shadowRoot.querySelector('label[for="' + checkbox.id + '"]').textContent
              : "")
        );
        data.interests.split(",").forEach(function (interest) {
          var normalized = normalizeLabel(interest);
          if (normalized && label.indexOf(normalized) > -1) {
            checkbox.checked = true;
            checkbox.dispatchEvent(new Event("change", { bubbles: true }));
          }
        });
      });
    }

    return delay(FILL_SETTLE_MS).then(function () {
      return shadowFormHasRequiredData(shadowRoot, data);
    });
  }

  function readShadowFieldValue(shadowRoot, selectors) {
    for (var i = 0; i < selectors.length; i++) {
      var field = shadowRoot.querySelector(selectors[i]);
      if (field && field.value && String(field.value).trim()) {
        return String(field.value).trim();
      }
    }
    return "";
  }

  function shadowEmbedHasCompanyField(shadowRoot) {
    return !!shadowRoot.querySelector(
      '[data-testid="field-custom#company"], input[name*="company" i]'
    );
  }

  function shadowFormHasRequiredData(shadowRoot, data) {
    if (!shadowRoot || !data.email) return false;

    var name = readShadowFieldValue(shadowRoot, ['[data-testid="field-first_name"]']);
    var email = readShadowFieldValue(shadowRoot, [
      '[data-testid="field-email"]',
      'input[type="email"]'
    ]);
    var phone = readShadowFieldValue(shadowRoot, [
      '[data-testid="field-phone_number"]',
      'input[type="tel"]',
      'input[name*="phone" i]'
    ]);
    var company = readShadowFieldValue(shadowRoot, [
      '[data-testid="field-custom#company"]',
      'input[name*="company" i]'
    ]);
    var message = readShadowFieldValue(shadowRoot, [
      'textarea[name="custom#message"]',
      'textarea[name*="message" i]',
      "textarea"
    ]);

    var phoneOk = isPhoneValidForForms(data.phone) || isPhoneValidForForms(phone);
    if (phone && !phoneOk) {
      phoneOk = digitsOnly(phone).length >= 9;
    }
    var companyRequired = shadowEmbedHasCompanyField(shadowRoot);
    var companyOk = !companyRequired || !!company;

    return !!(name && email && phoneOk && companyOk && message);
  }

  function monitorFormsApiSubmission(timeoutMs) {
    timeoutMs = timeoutMs || 12000;

    return new Promise(function (resolve) {
      var done = false;
      var originalFetch = window.fetch;
      var originalXhrOpen = XMLHttpRequest.prototype.open;
      var originalXhrSend = XMLHttpRequest.prototype.send;

      function finish(ok) {
        if (done) return;
        done = true;
        window.fetch = originalFetch;
        XMLHttpRequest.prototype.open = originalXhrOpen;
        XMLHttpRequest.prototype.send = originalXhrSend;
        resolve(!!ok);
      }

      function requestHasFormData(bodyStr) {
        if (!bodyStr) return false;
        return (
          bodyStr.indexOf("form_instance_id") > -1 &&
          bodyStr.indexOf('"email"') > -1 &&
          bodyStr.indexOf("first_name") > -1
        );
      }

      function inspectSubmission(url, bodyStr, responseOk) {
        if (String(url).indexOf("forms.shopifyapps.com/api/v2/form_submission") > -1) {
          finish(responseOk && requestHasFormData(bodyStr));
        }
      }

      window.fetch = function () {
        var url = arguments[0];
        var init = arguments[1] || {};
        var reqBody = init.body ? String(init.body) : "";
        var request = originalFetch.apply(this, arguments);

        if (String(url).indexOf("forms.shopifyapps.com/api/v2/form_submission") > -1) {
          request
            .then(function (res) {
              inspectSubmission(url, reqBody, res.ok);
            })
            .catch(function () {
              finish(false);
            });
        }

        return request;
      };

      XMLHttpRequest.prototype.open = function (method, url) {
        this.__oddfishFormsUrl = url;
        return originalXhrOpen.apply(this, arguments);
      };

      XMLHttpRequest.prototype.send = function (body) {
        var xhr = this;
        var url = xhr.__oddfishFormsUrl || "";
        var reqBody = body ? String(body) : "";

        if (String(url).indexOf("forms.shopifyapps.com/api/v2/form_submission") > -1) {
          xhr.addEventListener("loadend", function () {
            inspectSubmission(url, reqBody, xhr.status >= 200 && xhr.status < 300);
          });
        }

        return originalXhrSend.apply(this, arguments);
      };

      window.setTimeout(function () {
        finish(false);
      }, timeoutMs);
    });
  }

  function submitNativeContact(form) {
    function postContact() {
      return fetch("/contact", {
        method: "POST",
        body: new FormData(form),
        credentials: "same-origin",
        redirect: "follow",
        headers: {
          Accept: "text/html,application/xhtml+xml"
        }
      })
        .then(function (res) {
          if (!res.ok) return false;
          return res.text().then(function (html) {
            var lower = html.toLowerCase();
            if (lower.indexOf("posted_successfully") > -1) return true;
            if (lower.indexOf("form-success") > -1) return true;
            if (lower.indexOf("thank you") > -1 && lower.indexOf("form-error") === -1) return true;
            return res.redirected;
          });
        })
        .catch(function () {
          return false;
        });
    }

    if (form.getAttribute("data-nocaptcha") === "true") {
      return postContact();
    }

    if (window.Shopify && window.Shopify.captcha && typeof window.Shopify.captcha.protect === "function") {
      return new Promise(function (resolve) {
        window.Shopify.captcha.protect(form, function () {
          postContact().then(resolve);
        });
      });
    }

    return postContact();
  }

  function submitViaFormsApp(root, formId, data) {
    return getFormInstance(formId).then(function (instance) {
      return submitViaFormsApi(root, formId, data, instance);
    });
  }

  function getMetaobjectHealthUrl(apiUrl) {
    return apiUrl.replace(/\/contact-submission\/?$/, "/health");
  }

  function isMetaobjectApiHealthy(apiUrl) {
    var cacheKey = "oddfish_contact_api_ok";
    try {
      if (sessionStorage.getItem(cacheKey) === "true") {
        return Promise.resolve(true);
      }
    } catch (error) {
      /* sessionStorage unavailable */
    }

    var healthUrl = getMetaobjectHealthUrl(apiUrl);
    return fetch(healthUrl, { method: "GET", credentials: "omit" })
      .then(function (res) {
        return res.json().catch(function () {
          return null;
        });
      })
      .then(function (payload) {
        var ok = !!(payload && payload.ok);
        if (ok) {
          try {
            sessionStorage.setItem(cacheKey, "true");
          } catch (error) {
            /* ignore */
          }
        }
        return ok;
      })
      .catch(function () {
        return false;
      });
  }

  function saveToMetaobject(root, form, data) {
    var apiUrl = (root.getAttribute("data-contact-api-url") || "").trim();
    if (!apiUrl) return Promise.resolve(false);

    var secret = (root.getAttribute("data-contact-api-secret") || "").trim();

    return fetch(apiUrl, {
      method: "POST",
      headers: Object.assign(
        { "Content-Type": "application/json" },
        secret ? { "X-Contact-Secret": secret } : {}
      ),
      body: JSON.stringify({
        name: data.name,
        email: data.email,
        phone: formatPhoneForForms(data.phone) || data.phone,
        company: data.company,
        message: data.message,
        interests: data.interests,
        source: root.getAttribute("data-contact-source") || "website"
      })
    })
      .then(function (res) {
        if (res.ok) {
          try {
            sessionStorage.setItem("oddfish_contact_api_ok", "true");
          } catch (error) {
            /* ignore */
          }
        }
        return res.ok;
      })
      .catch(function () {
        return false;
      });
  }

  function setSubmitting(form, isSubmitting) {
    var btn = form.querySelector('[type="submit"], .form_btn');
    if (!btn) return;
    var isHomeContact = !!form.closest(".sixth_section_home");

    if (isSubmitting) {
      if (isHomeContact) {
        btn.classList.add("is-submitting");
        window.requestAnimationFrame(function () {
          if (form.dataset.oddfishSubmitting === "true") {
            btn.disabled = true;
            btn.setAttribute("aria-busy", "true");
          }
        });
        return;
      }

      btn.disabled = true;
      btn.setAttribute("aria-busy", "true");
      return;
    }

    btn.disabled = false;
    btn.classList.remove("is-submitting", "is-hovered");
    btn.setAttribute("aria-busy", "false");
  }

  function bindHomeContactSubmitFeedback(form) {
    if (!form.closest(".sixth_section_home")) return;

    var btn = form.querySelector('[type="submit"], .form_btn');
    if (!btn || btn.dataset.oddfishHomeSubmitFeedbackBound) return;
    btn.dataset.oddfishHomeSubmitFeedbackBound = "true";

    function showHover() {
      if (!btn.disabled) btn.classList.add("is-hovered");
    }

    function hideHover() {
      btn.classList.remove("is-hovered");
    }

    btn.addEventListener("mouseenter", showHover);
    btn.addEventListener("mouseleave", hideHover);
    btn.addEventListener("pointerdown", showHover);
    btn.addEventListener("pointerup", hideHover);
    btn.addEventListener("pointercancel", hideHover);
    btn.addEventListener("blur", hideHover);
  }

  function showFormSuccess(root, form) {
    var status = root.querySelector(".contact-form-status");
    var message =
      root.getAttribute("data-success-message") || "Thank you! We'll be in touch soon.";

    if (status) {
      status.innerHTML =
        '<p class="form-success" tabindex="-1">' + escapeHtml(message) + "</p>";
      status.setAttribute("data-success", "true");
      status.scrollIntoView({ behavior: "smooth", block: "center" });
      var successEl = status.querySelector(".form-success");
      if (successEl) successEl.focus();
    }

    form.reset();
  }

  function showFormError(root, message) {
    var status = root.querySelector(".contact-form-status");
    if (!status) return;
    var isSending = message === "Sending…";
    status.innerHTML =
      '<div class="form-error' +
      (isSending ? " form-sending" : "") +
      '">' +
      escapeHtml(message) +
      "</div>";
    status.removeAttribute("data-success");
  }

  function getFailureMessage(root, errorCode) {
    var hasMetaApi = !!(root.getAttribute("data-contact-api-url") || "").trim();

    if (errorCode === "forms-primary-failed") {
      if (formsRestrictedCache) {
        return (
          "Could not save in Apps → Forms because Shopify is blocking submissions on this store " +
          "(usually password-protected or not fully launched). Go to Online Store → Preferences and " +
          "turn off Password protection, then test again. " +
          (hasMetaApi
            ? "A backup copy was saved separately."
            : "Please email info@oddfishmedia.com.au directly.")
        );
      }

      if (lastFormsApiError) {
        return (
          "Could not save in Apps → Forms: " +
          lastFormsApiError +
          ". " +
          (hasMetaApi
            ? "A backup copy may have been saved separately."
            : "Please try again or email info@oddfishmedia.com.au directly.")
        );
      }

      return (
        "Could not save in Apps → Forms. Confirm the Forms block is in this section, " +
        "Theme settings → App embeds → Forms is enabled, and the form ID matches Apps → Forms. " +
        (hasMetaApi
          ? "A backup copy may have been saved separately."
          : "Please try again or email info@oddfishmedia.com.au directly.")
      );
    }

    if (hasMetaApi) {
      return (
        "Your message could not be saved. Email may still have been sent — " +
        "please try again or email info@oddfishmedia.com.au directly."
      );
    }

    return "Something went wrong sending your message. Please try again or email info@oddfishmedia.com.au directly.";
  }

  function disableShopifyCaptchaRedirect(form) {
    form.setAttribute("data-nocaptcha", "true");
  }

  function bindContactForm(root) {
    var form = root.querySelector(".oddfish-contact-page-form, .oddfish-contact-form");
    if (!form || form.dataset.oddfishFormsHandlerBound) return;

    form.dataset.oddfishFormsHandlerBound = "true";
    disableShopifyCaptchaRedirect(form);
    bindHomeContactSubmitFeedback(form);

    form.addEventListener(
      "submit",
      function (event) {
        event.preventDefault();
        event.stopPropagation();

        if (isThemeEditor()) {
          showFormError(
            root,
            "Form preview only — test submissions on the live site, not in the theme editor."
          );
          return;
        }

        if (form.dataset.oddfishSubmitting === "true") return;

        if (!form.checkValidity()) {
          form.reportValidity();
          return;
        }

        var data = getContactFormData(form);

        var phoneError = getPhoneValidationMessage(data.phone);
        if (phoneError) {
          showFormError(root, phoneError);
          return;
        }

        var formId = getFormId(root);
        if (!formId) {
          showFormError(
            root,
            "Homepage form is not configured. Add a Homepage Shopify Forms ID in this section’s settings (Apps → Forms → create “Homepage contact” form first)."
          );
          return;
        }

        form.dataset.oddfishSubmitting = "true";
        setSubmitting(form, true);
        showFormError(root, "Sending…");
        lastFormsApiError = "";
        formsRestrictedCache = null;

        var finished = false;

        function finishSuccess(formsOk, metaOk) {
          if (finished) return;
          finished = true;
          showFormSuccess(root, form);
          if (formsOk && !metaOk && (root.getAttribute("data-contact-api-url") || "").trim()) {
            console.warn(
              "[oddfish-contact] Saved in Forms but Metaobjects backup did not confirm."
            );
          }
          if (!formsOk && metaOk) {
            console.warn(
              "[oddfish-contact] Saved to backup storage; Forms app did not confirm."
            );
          }
        }

        function finishFailure(errorCode) {
          if (finished) return;
          finished = true;
          showFormError(
            root,
            getFailureMessage(root, errorCode === "forms-primary-failed" ? errorCode : "")
          );
        }

        function finishSubmitting() {
          delete form.dataset.oddfishSubmitting;
          setSubmitting(form, false);
        }

        getFormInstance(formId)
          .then(function () {
            var formsPromise = submitViaHiddenFormsEmbed(root, data);
            var metaPromise = saveToMetaobject(root, form, data);
            var emailPromise = submitNativeContact(form);

            return Promise.all([formsPromise, metaPromise, emailPromise]);
          })
          .then(function (results) {
            if (finished) return;

            var formsOk = results[0];
            var metaOk = results[1];
            var emailOk = results[2];

            if (formsOk || metaOk) {
              finishSuccess(formsOk, metaOk);
              return;
            }

            if (emailOk) {
              throw new Error("forms-primary-failed");
            }

            throw new Error("contact-failed");
          })
          .catch(function (error) {
            if (!finished) {
              finishFailure(error && error.message);
            }
          })
          .finally(finishSubmitting);
      },
      true
    );
  }

  function initRoot(root) {
    bindContactForm(root);
  }

  window.OddfishContactFormsSync = {
    bind: bindContactForm,
    init: initRoot
  };

  function boot() {
    clearShopifyContactPostedParam();
    document
      .querySelectorAll(".oddfish-contact-page-form, .oddfish-contact-form")
      .forEach(disableShopifyCaptchaRedirect);
    document
      .querySelectorAll(".oddfish-contact-page, .oddfish-home-page.sixth_section_home")
      .forEach(initRoot);
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", boot);
  } else {
    boot();
  }
})();
